import { useQuery, UseQueryOptions } from "@tanstack/react-query";

import { Feature } from "geojson";

import {
  CatchmentSettings,
  ECatchmentInputBy,
  ECatchmentTransport,
  ERoadAccessMeasurement,
  IsochroneData
} from "@/types/spatial";
import { getEffectiveRoadExclusions, iterateRows } from "@/utils/catchment";

import { getBulkIsochrone } from "./isochroneApi";

export const ISOCHORNE_KEYS = {
  isochrone: ["get-isochrone"] as const
} as const;

const isValidGetIsochronePayload = (payload: CatchmentSettings) => {
  const config = payload.roadAccessConfig;
  const datasets = payload.datasetConfig?.datasets ?? {};

  if (
    (!payload.placeCoordinates?.length && !Object.values(datasets)?.length) ||
    !config?.routingType ||
    !config.measurementType
  ) {
    return false;
  }

  // Check if distances or durations array exists, is not empty, and contains valid values
  if (config.measurementType === ERoadAccessMeasurement.DISTANCE) {
    return !!(
      config.distances?.length &&
      config.distances.length > 0 &&
      config.distances.some(
        (distance) => distance != null && String(distance).trim() !== ""
      )
    );
  } else {
    return !!(
      config.durations?.length &&
      config.durations.length > 0 &&
      config.durations.some(
        (duration) => duration != null && String(duration).trim() !== ""
      )
    );
  }
};

export const useGetBulkIsochroneQuery = (
  isNewRoadExclusionEnabled: boolean,
  payload: CatchmentSettings,
  options?: Omit<
    UseQueryOptions<IsochroneData[], Error, IsochroneData[]>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: [
      isNewRoadExclusionEnabled,
      ISOCHORNE_KEYS.isochrone,
      payload.catchmentInputBy,
      payload.roadAccessConfig?.distances,
      payload.roadAccessConfig?.durations,
      payload.roadAccessConfig?.measurementType,
      payload.roadAccessConfig?.routingType,
      payload.roadAccessConfig?.roadExclusions,
      payload.placeCoordinates,
      payload.datasetConfig
    ],
    queryFn: async () => {
      if (!isValidGetIsochronePayload(payload)) return [];

      const config = payload.roadAccessConfig;
      const metric = config?.measurementType as ERoadAccessMeasurement;

      const contours =
        metric === ERoadAccessMeasurement.DURATION
          ? config?.durations?.map(Number)
          : config?.distances?.map(Number);

      const getCoordinates = (): string[] => {
        if (payload.catchmentInputBy === ECatchmentInputBy.BY_LOCATION) {
          return (
            payload.placeCoordinates?.map(
              (coord) => `${coord.lng},${coord.lat}`
            ) ?? []
          );
        }

        if (payload.catchmentInputBy === ECatchmentInputBy.BY_DATASET) {
          const datasets = payload.datasetConfig?.datasets ?? {};
          const results: string[] = [];
          iterateRows(Object.values(datasets), (long, lat) => {
            results.push(`${long},${lat}`);
          });
          return results;
        }

        return [];
      };

      const coordinates = getCoordinates();
      if (!coordinates.length) return [];

      try {
        const [featureCollection] = await getBulkIsochrone({
          profile: config?.routingType as ECatchmentTransport,
          contour_metric: metric,
          contours: contours ?? [],
          excludes: getEffectiveRoadExclusions(
            config?.roadExclusions,
            config?.routingType,
            isNewRoadExclusionEnabled
          ),
          coordinates
        });

        // Group features by their 'coordinate' property
        const grouped = featureCollection?.features?.reduce<
          Record<string, Feature[]>
        >((acc, feature) => {
          const coordinate = feature.properties.coordinate; // this is from input (saved in properties from BE)
          const coordKey = `${coordinate[0]},${coordinate[1]}`;
          if (!acc[coordKey]) {
            acc[coordKey] = [];
          }
          acc[coordKey].push(feature);
          return acc;
        }, {});

        // Convert grouped features into an array of objects with position and FeatureCollection
        return coordinates.map((coordStr, index) => ({
          position: index + 1,
          data: [
            {
              type: "FeatureCollection" as const,
              features: grouped[coordStr] ?? []
            }
          ]
        }));
      } catch (error) {
        console.error("Isochrone fetch error:", error);
        return [{ data: [], position: 1, error: true }];
      }
    },
    ...options
  });
};
